import { LessonController, LessonPhases } from './lesson';
import { QuizOverlay } from './quiz/overlay';
import { showXpPopup, flashCard } from './quiz/feedback';
import { getUiSoundPlayer } from './ui-sounds';
import { loadBank, sampleBank } from './content/bank-loader';
import { loadProgress, saveProgress, createFreshProgress, addFactionXP, skillUnlocked } from '../shared/progress';
import { findOrCreateKP, recordAttempt } from '../shared/student-kp';
import { FACTIONS, factionForSubject } from '../shared/factions';
import { SKILL_CATALOG } from '../shared/skills';
import { mountEscapeButton } from './escape-button';
import { isProMode } from './settings';
import type { LearnDoneCallback } from './app';

export async function runEnglishLesson(container: HTMLElement, kpIdOrDone: string | LearnDoneCallback, onDone?: LearnDoneCallback): Promise<void> {
  // Overload resolution: legacy call signature runEnglishLesson(container, onDone)
  // New call signature: runEnglishLesson(container, kpId, onDone)
  let kpId: string;
  let done: LearnDoneCallback;
  if (typeof kpIdOrDone === 'function') {
    kpId = 'english.alphabet.phonics';
    done = kpIdOrDone;
  } else {
    kpId = kpIdOrDone;
    done = onDone!;
  }

  let url: string;
  if (kpId === 'english.alphabet.phonics') url = '/data/english-alphabet-翰林.json';
  else if (kpId === 'english.vocab.family') url = '/data/english-vocab-family-翰林.json';
  else if (kpId === 'english.phonics.basic') url = '/data/english-phonics-翰林.json';
  else if (kpId === 'english.vocab.school') url = '/data/english-vocab-school-翰林.json';
  else if (kpId === 'english.vocab.colors') url = '/data/english-colors-翰林.json';
  else if (kpId === 'english.dialogue.daily') url = '/data/english-greetings-翰林.json';
  // ── 三上 backport (grade: 3.1) — Y1.Q3 v1.1 第三批 ──────────────────
  else if (kpId === 'english.alphabet.review.up') url = '/data/english-alphabet-review-翰林-三上.json';
  else if (kpId === 'english.vocab.school.up') url = '/data/english-vocab-school-up-翰林.json';
  else if (kpId === 'english.vocab.colors.up') url = '/data/english-colors-up-翰林.json';
  else if (kpId === 'english.greetings.intro.up') url = '/data/english-greetings-up-翰林.json';
  // ── ch3 wip→ready KPs ────────────────────────────────────────────────────
  else if (kpId === 'english.vocab.food') url = '/data/english-vocab-food-翰林.json';
  else if (kpId === 'english.vocab.animals') url = '/data/english-vocab-animals-翰林.json';
  // ── ch4 wip→ready KPs ────────────────────────────────────────────────────
  else if (kpId === 'english.numbers.basic') url = '/data/english-numbers-翰林.json';
  else if (kpId === 'english.numbers.ordinal') url = '/data/english-numbers-ordinal-翰林.json';
  // ── ch5 wip→ready KPs ────────────────────────────────────────────────────
  else if (kpId === 'english.grammar.questions') url = '/data/english-grammar-questions-翰林.json';
  else if (kpId === 'english.grammar.statements') url = '/data/english-grammar-statements-翰林.json';
  // ── ch6 wip→ready KPs ────────────────────────────────────────────────────
  else if (kpId === 'english.dialogue.classroom') url = '/data/english-dialogue-classroom-翰林.json';
  // ── stage6 wire: meow911 段考真題池(對齊既有 chapter)─────────────
  else if (kpId === 'english.alphabet.phonics.meow911') url = '/data/meow911-english-ch1-phonics.json';
  else if (kpId === 'english.vocab.family.meow911') url = '/data/meow911-english-ch2-family.json';
  else if (kpId === 'english.vocab.food.meow911') url = '/data/meow911-english-ch3-food-animal-color.json';
  else if (kpId === 'english.numbers.basic.meow911') url = '/data/meow911-english-ch4-numbers.json';
  else if (kpId === 'english.grammar.questions.meow911') url = '/data/meow911-english-ch5-grammar.json';
  else if (kpId === 'english.meow911.misc') url = '/data/meow911-english-misc-pool.json';
  else throw new Error(`Unknown english KP: ${kpId}`);

  const bank = await loadBank(url);
  // Sample 10 questions, distinct surfaceVariants. Split: 2 warmup + 3 guide + 5 challenge
  const sampled = sampleBank(bank, 10);
  const phases: LessonPhases = {
    warmup: sampled.slice(0, 2),
    guide: sampled.slice(2, 5),
    challenge: sampled.slice(5, 10),
  };

  // Load (or create) progress early so we can record KP attempts during the lesson
  let progress = loadProgress();
  if (!progress) progress = createFreshProgress('指揮官');
  // v24 教學專業模式 — boss directive 2026-05-22: 教學要專業.
  const pro = isProMode(progress);
  const kp = findOrCreateKP(progress, kpId, 'english');

  const lesson = new LessonController(phases, 2, (correct) => {
    recordAttempt(kp, correct ? 'correct' : 'wrong', new Date().toISOString());
    saveProgress(progress!);
  }); // medium difficulty

  let overlay: QuizOverlay | null = new QuizOverlay({
    container,
    proMode: pro,
    onAnswer: (answer: number | string) => {
      const card = container.querySelector('.card') as HTMLElement | null;
      const result = lesson.submitAnswer(answer);
      if (!pro && card) flashCard(card, result.correct ? 'correct' : 'wrong');
      if (result.correct) {
        if (!pro) getUiSoundPlayer().play('answer-correct', 0.5);
      } else {
        if (!pro) getUiSoundPlayer().play('answer-wrong', 0.4);
      }
      if (!pro) {
        if (result.xp > 0) {
          const faction = FACTIONS[factionForSubject('english')];
          getUiSoundPlayer().play('xp-popup', 0.35);
          showXpPopup(`+${Math.round(result.xp)} ${faction.emoji}`, '#ffab40');
        } else if (!result.correct) {
          showXpPopup('沒關係 +2', '#80cbc4');
        }
      }
      setTimeout(() => {
        if (lesson.state === 'done') finish();
        else renderCurrent();
      }, result.correct ? 500 : 900);
    },
  });

  const unmountEscape = mountEscapeButton(() => {
    overlay?.destroy();
    overlay = null;
    unmountEscape();
    done({ newUnlocks: [], exited: true });
  });

  function renderCurrent(): void {
    if (!overlay) return;
    overlay.render(lesson.currentQuestion(), lesson.state, lesson.positionInPhase());
  }

  function finish(): void {
    unmountEscape();
    overlay?.destroy();
    overlay = null;
    // Reuse the progress object already held in closure (has KP updates from recordKP callbacks)
    const faction = factionForSubject('english');
    const xp = Math.round(lesson.totalXP);
    addFactionXP(progress!, faction, xp);
    // Generic unlock detection
    const newUnlocks: string[] = [];
    for (const skill of SKILL_CATALOG) {
      if (skill.faction === 'builtin') continue;
      if (skillUnlocked(progress!, skill.id)) continue;
      if (progress!.factionXP[skill.faction] >= skill.cost) {
        progress!.unlockedSkills.push(skill.id);
        newUnlocks.push(skill.id);
      }
    }
    saveProgress(progress!);
    done({ newUnlocks });
  }

  renderCurrent();
}
